import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LucidePencil, LucideAlertTriangle } from "lucide-react";
import { Modal, ModalBody, ModalFooter } from "@/components/modals/Modal";
import { REPORT_TYPES } from "@/constants/reportTypes";

function getSchoolYears() {
  const y = new Date().getFullYear();
  return [`${y - 1}-${y}`, `${y}-${y + 1}`, `${y + 1}-${y + 2}`];
}

const nativeSelect =
  "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background transition-colors focus:outline-none focus:ring-2 " +
  "focus:ring-ring focus:ring-offset-2 text-foreground";

/**
 * Modal for editing an existing Report Folder's name, grade, and school year.
 *
 * @param {object} props
 * @param {object|null} props.reportFolder - The Report Folder being edited, or null if closed
 * @param {() => void} props.onClose
 * @param {(reportFolder: object, editForm: object, opts: { cascadeGrade: boolean }) => Promise<void>} props.onSubmit
 * @param {boolean} props.updating
 */
export function EditReportFolderModal({ reportFolder, onClose, onSubmit, updating }) {
  const [editForm, setEditForm] = useState({
    name: "",
    grade: "",
    schoolYear: "",
  });
  const [confirmingCascade, setConfirmingCascade] = useState(false);

  const validGrades = (
    REPORT_TYPES.find((t) => t.value === reportFolder?.reportType)?.grades ?? []
  ).map(String);

  useEffect(() => {
    if (reportFolder) {
      const currentGrade = reportFolder.grade || "";
      setEditForm({
        name: reportFolder.name || "",
        grade: validGrades.includes(currentGrade) ? currentGrade : "",
        schoolYear: reportFolder.schoolYear || "",
      });
      setConfirmingCascade(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportFolder]);

  const schoolYears = getSchoolYears();
  const gradeChanged =
    !!reportFolder && editForm.grade !== (reportFolder.grade || "");

  async function handleSubmit(e) {
    e.preventDefault();
    if (!editForm.grade) return;
    if (gradeChanged) {
      setConfirmingCascade(true);
      return;
    }
    await onSubmit(reportFolder, editForm, { cascadeGrade: false });
  }

  async function handleConfirmCascade(cascadeGrade) {
    await onSubmit(reportFolder, editForm, { cascadeGrade });
  }

  if (confirmingCascade && reportFolder) {
    const reportsCount = reportFolder.reportsCount ?? 0;
    return (
      <Modal
        open={!!reportFolder}
        onClose={updating ? undefined : onClose}
        title="Update grade for all reports?"
        subtitle="Choose how to apply this grade change."
        icon={<LucideAlertTriangle size={16} className="text-amber-500" />}
        maxWidth="max-w-md"
        preventClose={updating}
      >
        <div className="px-6 py-5 space-y-4">
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 space-y-1">
            <p className="text-sm">
              Change grade from{" "}
              <span className="font-semibold text-foreground">
                {reportFolder.grade || "-"}
              </span>{" "}
              to{" "}
              <span className="font-semibold text-foreground">
                {editForm.grade}
              </span>
              .
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              You can apply this to the folder only, or also overwrite the
              grade on its report defaults and all{" "}
              <span className="font-medium text-foreground">
                {reportsCount} report{reportsCount === 1 ? "" : "s"}
              </span>{" "}
              inside it.
            </p>
          </div>

          <div className="flex flex-col gap-2 pt-1">
            <Button
              disabled={updating}
              onClick={() => handleConfirmCascade(true)}
              className="w-full"
            >
              {updating ? "Saving…" : "Yes - update folder, defaults, and all reports"}
            </Button>
            <Button
              variant="outline"
              disabled={updating}
              onClick={() => handleConfirmCascade(false)}
              className="w-full"
            >
              No - only update the folder
            </Button>
            <Button
              variant="ghost"
              disabled={updating}
              onClick={() => setConfirmingCascade(false)}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={!!reportFolder}
      onClose={onClose}
      title="Edit Report Folder"
      subtitle="Update the Report Folder name, grade, or school year."
      icon={<LucidePencil size={16} className="text-primary" />}
    >
      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <ModalBody className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">
              Report Folder Name{" "}
              <span className="text-muted-foreground font-normal text-xs">
                (optional)
              </span>
            </Label>
            <Input
              value={editForm.name}
              onChange={(e) =>
                setEditForm((p) => ({ ...p, name: e.target.value }))
              }
              className="h-10"
            />
          </div>
          <div className="flex gap-3">
            <div className="space-y-1.5 shrink-0" style={{ width: 96 }}>
              <Label className="text-sm font-medium">Grade</Label>
              <select
                value={editForm.grade}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, grade: e.target.value }))
                }
                className={nativeSelect}
                required
              >
                <option value="">-</option>
                {validGrades.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5 flex-1">
              <Label className="text-sm font-medium">School Year</Label>
              <select
                value={editForm.schoolYear}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, schoolYear: e.target.value }))
                }
                className={nativeSelect}
              >
                <option value="">Select year</option>
                {schoolYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button type="submit" disabled={updating || !editForm.grade} className="flex-1">
            {updating ? "Saving..." : "Save Changes"}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
